import FindDiscountCode from "@/module/DiscountCode/FindDiscountCode";
import CreateOrder from "@/module/Order/CreateOrder";
import CreateOrderIntent from "@/module/Order/CreateOrderIntent";
import CurrencyDeterminerFactory from "@/module/Form/Currency/CurrencyDeterminerFactory";
import ForeignVatProvider from "@/module/Form/ForeignVatProvider/ForeignVatProvider";
import ForeignCustomerDeterminer from "@/module/Form/VatDeterminer/ForeignCustomerDeterminer";
import ItemVatDeterminerFactory from "@/module/Form/VatDeterminer/VatStrategyFactory";
import IsVatPayer from "@/module/Form/IsVatPayer";
import ReverseChargeDeterminerFactory from "@/module/Form/ReverseCharge/ReverseChargeDeterminerFactory";
import DetectReverseCharge from "@/module/Form/ReverseCharge/DetectReverseCharge";
import IsTaxExempt from "@/module/Form/IsTaxExempt";
import FormResource from "@/module/Form/Resources/FormResource";
import CurrencyDeterminer from "@/module/Form/Currency/CurrencyDeterminer";
import ItemVatDeterminerStrategyFactory from "@/module/Form/VatDeterminer/VatStrategyFactory";
import ItemVatDeterminer from "@/module/Form/VatDeterminer/ItemVatDeterminer";
import SimplifiedInvoiceChecker from "@/module/Form/SimplifiedInvoiceChecker/SimplifiedInvoiceChecker";
import FapiTwisto from "@/module/Twisto/FapiTwisto";
import ExchangeRateProvider from "@/module/ExchangeRate/ExchangeRateProvider";
import StatisticsReporter from "@/module/StatisticsReporter/StatisticsReporter";
import CountryCurrencyDeterminer from "@/module/Form/Currency/CountryCurrencyDeterminer";

export default class ServiceLocator {
  private readonly formResource: FormResource;
  private _findDiscountCode?: FindDiscountCode;
  private _createOrderRequest?: CreateOrder;
  private _createOrderIntentRequest?: CreateOrderIntent;
  private _currencyDeterminer?: CurrencyDeterminer;
  private _countryCurrencyDeterminer?: CurrencyDeterminer;
  private _foreignVatProvider?: ForeignVatProvider;
  private _foreignCustomerDeterminer?: ForeignCustomerDeterminer;
  private _itemVatDeterminerFactory?: ItemVatDeterminerStrategyFactory;
  private _isVatPayer?: IsVatPayer;
  private _reverseChargeDeterminerFactory?: ReverseChargeDeterminerFactory;
  private _reverseChargeDeterminer?: DetectReverseCharge;
  private _isTaxExempt?: IsTaxExempt;
  private _itemVatDeterminer?: ItemVatDeterminer;
  private _simplifiedInvoiceChecker?: SimplifiedInvoiceChecker;
  private _twisto?: FapiTwisto;
  private _exchangeRateProvider?: ExchangeRateProvider;
  private _statisticsReporter?: StatisticsReporter;

  constructor(formResource: FormResource) {
    this.formResource = formResource;
  }

  get findDiscountCode(): FindDiscountCode {
    if (this._findDiscountCode === undefined) {
      this._findDiscountCode = new FindDiscountCode(
        this.formResource.baseUrl,
        this.formResource.path,
      );
    }

    return this._findDiscountCode;
  }

  get createOrderRequest(): CreateOrder {
    if (this._createOrderRequest === undefined) {
      this._createOrderRequest = new CreateOrder(
        this.formResource.baseUrl,
        this.formResource.path,
      );
    }

    return this._createOrderRequest;
  }

  get createOrderIntentRequest(): CreateOrderIntent {
    if (this._createOrderIntentRequest === undefined) {
      this._createOrderIntentRequest = new CreateOrderIntent(
        this.formResource.baseUrl,
        this.formResource.path,
      );
    }

    return this._createOrderIntentRequest;
  }

  get currencyDeterminer(): CurrencyDeterminer {
    if (this._currencyDeterminer === undefined) {
      this._currencyDeterminer = CurrencyDeterminerFactory.create(
        this.formResource.currency_setting,
        this.formResource.currency_code,
        this.formResource.allowed_currencies,
        this.formResource.country_currency_setting,
      );
    }

    return this._currencyDeterminer;
  }

  get countryCurrencyDeterminer(): CurrencyDeterminer {
    if (this._countryCurrencyDeterminer === undefined) {
      this._countryCurrencyDeterminer = new CountryCurrencyDeterminer(
        this.formResource.country_currency_setting,
      );
    }

    return this._countryCurrencyDeterminer;
  }

  get foreignVatProvider(): ForeignVatProvider {
    if (this._foreignVatProvider === undefined) {
      this._foreignVatProvider = new ForeignVatProvider(
        this.formResource.foreign_settings,
      );
    }

    return this._foreignVatProvider;
  }

  get foreignCustomerDeterminer(): ForeignCustomerDeterminer {
    if (this._foreignCustomerDeterminer === undefined) {
      this._foreignCustomerDeterminer = new ForeignCustomerDeterminer(
        this.formResource.user_country,
      );
    }

    return this._foreignCustomerDeterminer;
  }

  get itemVatDeterminerFactory(): ItemVatDeterminerFactory {
    if (this._itemVatDeterminerFactory === undefined) {
      this._itemVatDeterminerFactory = new ItemVatDeterminerFactory(
        this.formResource,
        this.foreignCustomerDeterminer,
        this.foreignVatProvider,
      );
    }

    return this._itemVatDeterminerFactory;
  }

  get itemVatDeterminer(): ItemVatDeterminer {
    if (this._itemVatDeterminer === undefined) {
      this._itemVatDeterminer = this.itemVatDeterminerFactory.getStrategy(
        this.formResource.settings.vat_mode,
      );
    }

    return this._itemVatDeterminer;
  }

  get isVatPayer(): IsVatPayer {
    if (this._isVatPayer === undefined) {
      this._isVatPayer = new IsVatPayer(
        this.foreignCustomerDeterminer,
        this.formResource.settings.vat_mode,
      );
    }

    return this._isVatPayer;
  }

  get reverseChargeDeterminerFactory(): ReverseChargeDeterminerFactory {
    if (this._reverseChargeDeterminerFactory === undefined) {
      this._reverseChargeDeterminerFactory = new ReverseChargeDeterminerFactory(
        this.foreignCustomerDeterminer,
        this.formResource.baseUrl,
        this.formResource.path,
      );
    }

    return this._reverseChargeDeterminerFactory;
  }

  get reverseChargeDeterminer(): DetectReverseCharge {
    if (this._reverseChargeDeterminer === undefined) {
      this._reverseChargeDeterminer = new DetectReverseCharge(
        this.formResource.reverse_charge,
        this.foreignCustomerDeterminer,
        this.reverseChargeDeterminerFactory,
      );
    }

    return this._reverseChargeDeterminer;
  }

  get isTaxExempt(): IsTaxExempt {
    if (this._isTaxExempt === undefined) {
      this._isTaxExempt = new IsTaxExempt(this.foreignCustomerDeterminer);
    }

    return this._isTaxExempt;
  }

  get simplifiedInvoiceChecker(): SimplifiedInvoiceChecker {
    if (this._simplifiedInvoiceChecker === undefined) {
      this._simplifiedInvoiceChecker = new SimplifiedInvoiceChecker();
    }

    return this._simplifiedInvoiceChecker;
  }

  get twisto(): FapiTwisto {
    if (this._twisto === undefined) {
      this._twisto = new FapiTwisto(this.formResource);
    }

    return this._twisto;
  }

  get exchangeRateProvider(): ExchangeRateProvider {
    if (this._exchangeRateProvider === undefined) {
      this._exchangeRateProvider = new ExchangeRateProvider(
        this.formResource.exchange_rates,
        this.formResource.baseUrl,
        this.formResource.path,
        this.formResource.settings.currency,
      );
    }

    return this._exchangeRateProvider;
  }

  get statisticsReporter(): StatisticsReporter {
    if (this._statisticsReporter === undefined) {
      this._statisticsReporter = new StatisticsReporter(
        this.formResource.baseUrl,
        this.formResource.path,
      );
    }

    return this._statisticsReporter;
  }
}
